/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import React, { useState, useEffect } from "react";

import {
    Button,
    CardContent,
    CircularProgress,
    Grid,
    InputAdornment,
    Paper,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Box from "@mui/material/Box";

import PersonIcon from "@mui/icons-material/Person";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import UserInfoPanel from "./UserInfoPanel";
import CreateUserModal from "./CreateUserModal";
import { DetailedUser } from "../../../utils/Interfaces";
import { selectExistingUsers } from "./userManagementSlice";
import { filterUsersByName, refreshUsers } from "./userManagementFunctions";

import SearchIcon from "@mui/icons-material/Search";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { customScrollbarStyle } from "../../../utils/Functions";
import Card from "@mui/material/Card";
import StyledBreadcrumbs from "../../../components/general/StyledBreadcrumbs";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";

const UserManagement = () => {
    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const users: DetailedUser[] = useAppSelector(selectExistingUsers);

    const [selectedUserIndex, setSelectedUserIndex] = useState<number>(0);

    const [createUserModalOpen, setCreateUserModalOpen] = useState<boolean>(false);

    const [userFilter, setUserFilter] = useState<string>("");

    const [loading, setLoading] = useState<boolean>(false);

    const [filteredUsers, setFilteredUsers] = useState<DetailedUser[]>(users);

    useEffect(() => {
        setFilteredUsers(filterUsersByName(users, userFilter));
    }, [users, userFilter]);

    useEffect(() => {
        refreshUsers(dispatch, setLoading);
    }, [dispatch]);

    if (loading) {
        return (
            <>
                <StyledBreadcrumbs
                    paths={[
                        { text: "Techwise Portal", route: "/" },
                        { text: "Admin", route: "/?section=admin" },
                        { text: "", route: "/admin/users" },
                    ]}
                />

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            marginTop: "20vh",
                        }}
                    >
                        <CircularProgress size={40} />
                    </Box>
                </Box>
            </>
        );
    }

    return (
        <>
            <StyledBreadcrumbs
                paths={[
                    { text: "Techwise Portal", route: "/" },
                    { text: "Admin", route: "/?section=admin" },
                    { text: "User Management", route: "/admin/users" },
                ]}
            />

            <Grid container direction="row" justifyContent="flex-start">
                <CreateUserModal
                    open={createUserModalOpen}
                    handleClose={() => setCreateUserModalOpen(false)}
                    // Re-pull the data when adding users.
                    onRefreshRequested={() => refreshUsers(dispatch, setLoading)}
                />

                <Grid item xs={4}>
                    <Box sx={{ mt: 2, mb: 2 }}>
                        <Stack direction="row" justifyContent="space-between">
                            <Stack direction="column" justifyContent="center">
                                <Tooltip title="Add a New User to the System">
                                    <Button
                                        variant="contained"
                                        onClick={() => setCreateUserModalOpen(true)}
                                        sx={{ p: 1.5 }}
                                        size="small"
                                    >
                                        <Stack direction="row">
                                            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                                                <PersonAddIcon fontSize="small" />
                                            </Box>
                                            <Box
                                                sx={{ ml: 2, display: "flex", flexDirection: "column", justifyContent: "center" }}
                                            >
                                                <Typography>Add New User</Typography>
                                            </Box>
                                        </Stack>
                                    </Button>
                                </Tooltip>
                            </Stack>
                            <Box>
                                <TextField
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                        setUserFilter(event.target.value);
                                        setFilteredUsers(filterUsersByName(users, event.target.value));
                                    }}
                                    margin="normal"
                                    fullWidth
                                    id="user_filter"
                                    label={"Filter Users"}
                                    name="user_filter"
                                    size="small"
                                    value={userFilter}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment disableTypography position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </Stack>
                    </Box>
                    <Paper sx={{ borderRadius: "8px" }}>
                        <Box sx={{ maxHeight: "calc(90vh - 213px)", overflowY: "auto", ...customScrollbarStyle() }}>
                            {filteredUsers.length === 0 && (
                                <Card>
                                    <CardContent>
                                        <Typography textAlign="center">{`No users found matching "${userFilter}".`}</Typography>
                                    </CardContent>
                                </Card>
                            )}
                            {filteredUsers.length > 0 && (
                                <List>
                                    {filteredUsers.map((user, index) => {
                                        return (
                                            <ListItem
                                                disablePadding
                                                key={index}
                                                selected={selectedUserIndex === index}
                                                onClick={() => setSelectedUserIndex(index)}
                                            >
                                                <Tooltip
                                                    title={`Select ${user.first_name} ${user.last_name} (@${user.username})`}
                                                >
                                                    <ListItemButton>
                                                        <ListItemIcon>
                                                            {user.active ? <PersonIcon /> : <PersonOffIcon color="warning" />}
                                                        </ListItemIcon>
                                                        <ListItemText primary={`${user.first_name} ${user.last_name}`} />
                                                    </ListItemButton>
                                                </Tooltip>
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={8}>
                    <Box>
                        {users.length > 0 && (
                            <UserInfoPanel
                                user={filteredUsers.length === 0 ? null : filteredUsers[selectedUserIndex]}
                                existingUsers={users}
                                onRemoveUser={() => refreshUsers(dispatch, setLoading)}
                            />
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default UserManagement;
